import React from "react";
import { Icon } from "@mui/material";

export interface Step {
  id: string;
  name: string;
  description: string;
  status: "complete" | "current" | "upcoming";
  icon?: string;
}

interface StepperProps {
  steps: Step[];
  onStepClick?: (stepId: string) => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const Stepper: React.FC<StepperProps> = ({ steps, onStepClick }) => {
  return (
    <div className="lg:border-t lg:border-b lg:border-gray-200">
      <nav
        aria-label="Progress"
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      >
        <ol
          role="list"
          className="overflow-hidden rounded-md lg:flex lg:rounded-none lg:border-r lg:border-l lg:border-gray-200"
        >
          {steps.map((step, stepIdx) => (
            <li key={step.id} className="relative overflow-hidden lg:flex-1">
              <div
                className={classNames(
                  stepIdx === 0 ? "rounded-t-md border-b-0" : "",
                  stepIdx === steps.length - 1 ? "rounded-b-md border-t-0" : "",
                  "overflow-hidden border border-gray-200 lg:border-0"
                )}
              >
                {step.status === "complete" ? (
                  <div
                    onClick={() => onStepClick?.(step.id)}
                    className="group cursor-pointer"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-0 h-full w-1 bg-transparent group-hover:bg-[#E5F7FB] lg:top-auto lg:bottom-0 lg:h-1 lg:w-full"
                    />
                    <span
                      className={classNames(
                        stepIdx !== 0 ? "lg:pl-9" : "",
                        "flex items-start px-6 py-5 text-sm font-medium"
                      )}
                    >
                      <span className="shrink-0">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0891B2]">
                          <Icon className="h-6 w-6 text-white">check</Icon>
                        </span>
                      </span>
                      <span className="mt-0.5 ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-[#0891B2]">
                          {step.name}
                        </span>
                        <span className="text-sm font-medium text-gray-500">
                          {step.description}
                        </span>
                      </span>
                    </span>
                  </div>
                ) : step.status === "current" ? (
                  <div
                    onClick={() => onStepClick?.(step.id)}
                    aria-current="step"
                    className="cursor-pointer"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-0 h-full w-1 bg-[#0891B2] lg:top-auto lg:bottom-0 lg:h-1 lg:w-full"
                    />
                    <span
                      className={classNames(
                        stepIdx !== 0 ? "lg:pl-9" : "",
                        "flex items-start px-6 py-5 text-sm font-medium"
                      )}
                    >
                      <span className="shrink-0">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#0891B2]">
                          <span className="text-[#0891B2]">
                            <Icon>{step.icon || step.id}</Icon>
                          </span>
                        </span>
                      </span>
                      <span className="mt-0.5 ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-[#0891B2]">
                          {step.name}
                        </span>
                        <span className="text-sm font-medium text-gray-500">
                          {step.description}
                        </span>
                      </span>
                    </span>
                  </div>
                ) : (
                  <div
                    onClick={() => onStepClick?.(step.id)}
                    className="group cursor-pointer"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-0 h-full w-1 bg-transparent group-hover:bg-[#E5F7FB] lg:top-auto lg:bottom-0 lg:h-1 lg:w-full"
                    />
                    <span
                      className={classNames(
                        stepIdx !== 0 ? "lg:pl-9" : "",
                        "flex items-start px-6 py-5 text-sm font-medium"
                      )}
                    >
                      <span className="shrink-0">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#CBD5E1]">
                          <span className="text-[#64748B]">
                            <Icon>{step.icon || step.id}</Icon>
                          </span>
                        </span>
                      </span>
                      <span className="mt-0.5 ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-[#64748B]">
                          {step.name}
                        </span>
                        <span className="text-sm font-medium text-[#94A3B8]">
                          {step.description}
                        </span>
                      </span>
                    </span>
                  </div>
                )}

                {stepIdx !== 0 ? (
                  <>
                    {/* Separator */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 top-0 left-0 hidden w-3 lg:block"
                    >
                      <svg
                        fill="none"
                        viewBox="0 0 12 82"
                        preserveAspectRatio="none"
                        className="h-full w-full text-[#CBD5E1]"
                      >
                        <path
                          d="M0.5 0V31L10.5 41L0.5 51V82"
                          stroke="currentcolor"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                    </div>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};
